// Daily puzzle assembly: Sydney date → seed → level.
//
// Layouts are hand-authored templates picked by seed, with seeded variation on top. Fully
// procedural paddocks are a later option — curated templates guarantee solvability and sane par
// times with far less machinery than a generator that must be proven safe.

import { dailyKey, dailySeed, dayNumber, hashString, makeRng, rngInt, rngRange } from './seed'
import type { Level } from './types'
import { TEMPLATES } from './templates'

export interface Daily {
  key: string
  seed: number
  day: number
  level: Level
}

export function buildDaily(now: Date = new Date()): Daily {
  const key = dailyKey(now)
  return buildDailyForKey(key)
}

/**
 * Day of week for a daily key: 0 = Sunday … 6 = Saturday.
 *
 * The key is already the Sydney calendar date, so this is plain arithmetic on that string parsed as
 * UTC — never the host clock, or the server would build a different level than the player did.
 */
export function weekdayOf(key: string): number {
  return new Date(`${key}T00:00:00Z`).getUTCDay()
}

/**
 * The week's difficulty curve, crossword-style: gentle Monday, hardest Saturday, Sunday a
 * medium-hard showcase rather than a peak.
 *
 * Bands are difficulty values (see `Template.difficulty`), and a day may allow more than one so
 * there is still variety within a rung. Morsel ships an ungraded word list and flags it as a gap;
 * PLAN.md § M4 says explicitly not to inherit the same TODO.
 */
const WEEK_CURVE: readonly (readonly number[])[] = [
  [3, 4], // Sun — showcase
  [1, 2], // Mon — gentlest
  [2], //    Tue
  [2, 3], // Wed
  [3], //    Thu
  [3, 4], // Fri
  [4, 5], // Sat — hardest
]

export function buildDailyForKey(key: string): Daily {
  const seed = dailySeed(key)
  const rng = makeRng(seed)

  // Pick the day's difficulty band first, then a template within it. Exactly one RNG draw either
  // way, so the stream shape is unchanged from the ungraded version.
  const band = WEEK_CURVE[weekdayOf(key)]
  const candidates = TEMPLATES.filter((t) => band.includes(t.difficulty))
  // A band with no templates would silently narrow the week to whatever is left; fall back to the
  // full set rather than crashing a daily, and let the coverage test catch it in CI.
  const pool = candidates.length > 0 ? candidates : TEMPLATES

  const template = pool[rngInt(rng, 0, pool.length - 1)]
  const level = template.build(rng)

  return { key, seed, day: dayNumber(key), level }
}

export interface TemplateSample {
  seed: number
  level: Level
}

/**
 * One sample of a single template, for par measurement and geometry tests.
 *
 * Par has to come from a median over many members of a template's family, but the daily path picks
 * templates by RNG — across 30 days, ten templates get 2-4 samples each, which is not a median.
 * This draws from one template directly so it can be sampled to any depth.
 *
 * The throwaway draw is deliberate and load-bearing: `buildDailyForKey` consumes one value picking
 * the template *before* calling `build`, so a daily's `build` always sees an RNG that has already
 * advanced once. Matching that here keeps sampled levels drawn from the same generator states as
 * real dailies — without it, par would be measured against a subtly different family than the one
 * that ships.
 */
export function buildTemplateSample(templateId: string, index: number): TemplateSample {
  const template = TEMPLATES.find((t) => t.id === templateId)
  if (!template) {
    throw new Error(
      `Unknown template "${templateId}". Known: ${TEMPLATES.map((t) => t.id).join(', ')}`,
    )
  }
  const seed = hashString(`bigdog:sample:${templateId}:${index}`)
  const rng = makeRng(seed)
  rng() // mirrors the daily's template-selection draw; see above.
  return { seed, level: template.build(rng) }
}

/**
 * Seeded scatter of the flock spawn inside the template's legal band. Kept here rather than in the
 * templates so every template varies the same way.
 */
export function jitterSpawn(
  rng: () => number,
  base: { x: number; y: number; w: number; h: number },
  amount: number,
): { x: number; y: number; w: number; h: number } {
  return {
    x: base.x + rngRange(rng, -amount, amount),
    y: base.y + rngRange(rng, -amount, amount),
    w: base.w,
    h: base.h,
  }
}
